import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSignUp } from "@clerk/clerk-react";
import AuthLayout from "../components/AuthLayout";
import BackButton from "../components/BackButton";
import {
  CONSENT_ROWS,
  ConsentTerms,
  documentsOf,
} from "../components/ConsentTerms";
import { Button } from "../../../components/ui";
import googleIcon from "../../../assets/svg/auth/google.svg";
import {
  isEmailTakenError,
  signup,
  startGoogleSignup,
} from "../services/authService";
import { useRestoreFromRedirect } from "../hooks/useRestoreFromRedirect";
import { rememberGivenConsent } from "../services/consentService";
import { ApiError } from "../../../lib/http";
import { ROUTES } from "../../../routes/paths";
import { traceRedirect } from "../../../routes/routeTrace";
import type { ConsentState } from "../types/auth.types";
import type { ConsentRowKey } from "../components/ConsentTerms";

/**
 * What went wrong on the way to creating the account.
 *
 * An address that is already registered is kept apart from the rest, because
 * it is the only one the user cannot get past by trying again - the account
 * exists, so the way forward is the log-in screen, not this one.
 */
type SubmitError =
  | { kind: "emailTaken" }
  | { kind: "other"; message: string };

/**
 * The DPDP consent gate, and the screen that actually creates the account.
 *
 * The sign-up screen collects details and then sends the user here without
 * calling Clerk at all, so no account exists anywhere until every box below is
 * ticked - which is the point: consent has to precede the processing, not
 * follow it. From here the email route goes on to the OTP screen and the
 * Google route leaves for Google's own consent screen.
 */
const ConsentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ConsentState | null;

  // One resource for both routes: each of them creates the account, so each of
  // them is a sign-up. The Google route only differs in leaving for Google
  // first and finishing on the way back.
  const { isLoaded: clerkLoaded, signUp } = useSignUp();

  // A new account owes every document, so the screen asks for all of them.
  const [agreed, setAgreed] = useState<Partial<Record<ConsentRowKey, boolean>>>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<SubmitError | null>(null);

  // The Google route leaves for Google with `submitting` still set, since
  // nothing after the redirect runs. Coming back must hand the screen over.
  useRestoreFromRedirect(() => setSubmitting(false));

  const isGoogle = state?.method === "google";
  const allAgreed = CONSENT_ROWS.every((row) => agreed[row.key]);

  const tick = (key: ConsentRowKey, checked: boolean) => {
    setAgreed((previous) => ({ ...previous, [key]: checked }));
    setError(null);
  };

  const handleContinue = async () => {
    // `submitting` among the guards, not just on the button: this creates the
    // account, and a second press while the first call is still out starts a
    // second sign-up for the same address - which Clerk answers by saying the
    // address is taken, on the screen of the user who is in the middle of
    // taking it.
    if (submitting || !allAgreed || !clerkLoaded || !signUp) return;

    setError(null);
    setSubmitting(true);

    // Parked before anything is created, because from here on the screen may
    // not survive: the Google route leaves the app outright, and Clerk can
    // finish the email route with a top-level redirect. Recording it now is not
    // an option either - there is no session to attach it to until the account
    // exists, which is what the next line starts. `useAccountSync` posts it.
    //
    // A browser that refuses storage leaves the documents outstanding rather
    // than the sign-up blocked: the backend's answer stays the truthful one,
    // and the user is asked again rather than being shut out over something
    // they cannot act on.
    const consented = documentsOf(CONSENT_ROWS.filter((row) => agreed[row.key]));
    if (!rememberGivenConsent(consented)) {
      console.warn(
        "Consent could not be stored for submission - it will be requested again."
      );
    }

    try {
      if (isGoogle) {
        // Leaves the app entirely; Clerk returns to ROUTES.ssoCallback with a
        // sign-up already under way, which that screen only has to finish.
        // Nothing after this runs.
        //
        // The trial rather than the dashboard, the same as the email route's
        // end: this is a new account either way, and the offer is the first
        // thing one is shown. The callback screen agrees — see the sign-up
        // redirect URLs it hands Clerk.
        await startGoogleSignup(signUp, ROUTES.trial, state?.fullName);
        return;
      }

      await signup(signUp, state?.fullName ?? "", state?.email ?? "");
      navigate(ROUTES.verifyOtp, {
        state: {
          email: state?.email,
          fullName: state?.fullName,
          flow: "signup",
        },
      });
    } catch (caught) {
      setError(
        isEmailTakenError(caught)
          ? { kind: "emailTaken" }
          : {
              kind: "other",
              message:
                caught instanceof ApiError
                  ? caught.message
                  : "Network error. Please check your connection and try again.",
            }
      );
      setSubmitting(false);
      console.error("Consent submit error:", caught);
    }
  };

  // Guard: reachable only with the state the sign-up screen passes. An email
  // sign-up with no details behind it would produce nothing but a Clerk error.
  const detailsMissing =
    state?.method === "email" && (!state.fullName || !state.email);
  if (!state || detailsMissing) {
    traceRedirect(
      ROUTES.consent,
      ROUTES.signup,
      state ? "details missing from the state" : "no router state"
    );
    return <Navigate to={ROUTES.signup} replace />;
  }

  return (
    <AuthLayout wide showFooter={false}>
      <BackButton className="mb-5" />

      <h1 className="text-[24px] leading-tight font-semibold text-[#1F2937]">
        Before we continue
      </h1>
      <p className="mt-1 text-[14px] text-[#62636C]">
        Required under India&rsquo;s DPDP Act, 2023.
      </p>

      <h2 className="mt-6 text-[16px] font-medium text-[#1E1F24]">
        Let&rsquo;s sign below terms
      </h2>

      <ConsentTerms rows={CONSENT_ROWS} agreed={agreed} onToggle={tick} />

      {/* Clerk's bot check mounts here rather than on the sign-up screen,
          because this is where the account is created - `signUp.create` on the
          email route, `authenticateWithRedirect` on the Google one - and
          without the element in the DOM at that moment a custom sign-up fails
          whenever Smart CAPTCHA is on.

          Clerk mounts it as part of that call, which is to say the moment the
          button is pressed, and a widget appearing out of nothing would shove
          the button out from under the cursor that just clicked it. So the
          space is taken as the submit starts rather than when the widget
          lands: `min-h` is the height of Turnstile's normal widget, held from
          the click until the screen is done with. Idle, it collapses to
          nothing - and it stays collapsed throughout on an instance whose bot
          protection is invisible, which renders no widget to make room for. */}
      <div
        id="clerk-captcha"
        className={`mt-4 ${submitting ? "min-h-[65px]" : "empty:hidden"}`}
      />

      {error?.kind === "emailTaken" && (
        <div className="mt-6 rounded-lg border border-[#F5C6C6] bg-[#FEF4F4] px-4 py-3.5">
          <p className="text-[14px] font-medium text-[#B42318]">
            You already have an account with this email
          </p>
          <p className="mt-1 text-[13px] leading-5 text-[#8C3A3A]">
            {state.email ? (
              <>
                <span className="font-medium">{state.email}</span> is already
                registered.{" "}
              </>
            ) : (
              "That address is already registered. "
            )}
            Signing up creates a new account, so it cannot be used here - please
            log in instead.
          </p>
        </div>
      )}

      {error?.kind === "other" && (
        <p className="mt-6 text-center text-xs text-red-500">{error.message}</p>
      )}

      <div className="mt-8 flex flex-col items-center gap-3">
        {/* Offering to create the account again would only fail the same way,
            so the registered address gets the one action that works. */}
        {error?.kind === "emailTaken" ? (
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate(ROUTES.login)}
            className="w-[380px] max-w-full"
          >
            Go to log in
          </Button>
        ) : (
          <Button
            variant="primary"
            size="lg"
            onClick={handleContinue}
            loading={submitting}
            disabled={!allAgreed || !clerkLoaded}
            className="w-[380px] max-w-full"
            startIcon={
              isGoogle ? (
                <img src={googleIcon} alt="" className="w-5 h-5" />
              ) : undefined
            }
          >
            {isGoogle ? "Continue with Google" : "Agree & create account"}
          </Button>
        )}

        {/* Only once something has gone wrong, and only on the email route -
            the details it goes back to are the ones the form collected. */}
        {error && !isGoogle && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.signup)}
          >
            {error.kind === "emailTaken"
              ? "Sign up with a different email"
              : "Go back and edit your details"}
          </Button>
        )}
      </div>
    </AuthLayout>
  );
};

export default ConsentPage;
